/**
 * services/telemetry.js — Bar Experts
 * Serviço leve de telemetria e rastreamento de acessos a páginas públicas.
 * Utiliza fetch com keepalive e fallback para navigator.sendBeacon para zero impacto de performance.
 */
import axios from 'axios'

let lastRecordedPath = ''
let lastRecordedTime = 0

export function trackPageView(path, options = {}) {
  if (typeof window === 'undefined') return

  const now = Date.now()
  let cleanPath = (path || '/').split('?')[0].split('#')[0].trim()
  if (!cleanPath || cleanPath === '') {
    cleanPath = '/'
  }

  // Evita pings duplicados na mesma rota em menos de 3 segundos
  if (cleanPath === lastRecordedPath && (now - lastRecordedTime) < 3000) {
    return
  }

  lastRecordedPath = cleanPath
  lastRecordedTime = now

  // Detecção leve de dispositivo
  const width = window.innerWidth || document.documentElement.clientWidth || 1200
  let deviceType = 'desktop'
  if (width <= 768) {
    deviceType = 'mobile'
  } else if (width <= 1024) {
    deviceType = 'tablet'
  }

  // Extrai utm_source se houver
  let explicitSource = null
  try {
    const urlParams = new URLSearchParams(window.location.search)
    explicitSource = urlParams.get('utm_source') || urlParams.get('source')
  } catch (e) {
    // ignore
  }

  const payload = {
    page_path: cleanPath,
    referer: document.referrer || '',
    source: explicitSource,
    device_type: deviceType,
    is_admin: Boolean(options.isAdmin)
  }

  const endpoint = '/api/v1/telemetry/record'

  // Prioriza fetch assíncrono com keepalive para máxima confiabilidade em todos os navegadores
  try {
    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(() => {
      // Fallback para sendBeacon caso fetch falhe
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
        navigator.sendBeacon(endpoint, blob)
      }
    })
  } catch (err) {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
        navigator.sendBeacon(endpoint, blob)
      } catch (e) {
        // silencioso para não interromper a UX do usuário
      }
    }
  }
}

/**
 * Busca estatísticas de telemetria (exclusivo para depinhojr / Super Admin)
 */
export async function fetchTelemetryStats(period = 'all', pageFilter = null, includeAdmin = false) {
  const token = localStorage.getItem('be_token') || sessionStorage.getItem('be_token') || localStorage.getItem('token')
  if (!token) {
    const err = new Error('Sua sessão expirou ou você não está autenticado. Faça login como Administrador para visualizar a telemetria.')
    err.response = { status: 401, data: { error: err.message, expired: true } }
    throw err
  }

  let url = `/api/v1/telemetry/stats?period=${period}`
  if (pageFilter) {
    url += `&page=${encodeURIComponent(pageFilter)}`
  }
  if (includeAdmin) {
    url += `&include_admin=true`
  }

  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
  return response.data
}

